import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Calendar, Image as ImageIcon, Upload, FileUp, RefreshCcw } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import DoctorHeader from '@/components/doctor/DoctorHeader';
import DoctorFooterNav from '@/components/doctor/DoctorFooterNav';
import PatientHeader from '@/components/caseFile/PatientHeader';
import PatientInfoCard from '@/components/caseFile/PatientInfoCard';
import TreatmentHistoryCard from '@/components/caseFile/TreatmentHistoryCard';
import PatientTabView from '@/components/caseFile/PatientTabView';
import { useFileUpload } from '@/hooks/useFileUpload';
import { useCaseFile } from '@/hooks/useCaseFile';
import ROUTES from '@/routes-map';
import { Users, AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const CaseFilePage = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { uploadFile } = useFileUpload();
  const [treatments, setTreatments] = useState<any[]>([]);
  const [isLoadingTreatments, setIsLoadingTreatments] = useState(false);

  // Fetch treatments for this patient
  useEffect(() => {
    const fetchTreatmentHistory = async () => {
      if (!patientId) return;
      
      setIsLoadingTreatments(true);
      try {
        console.log("Fetching treatments for patient:", patientId);
        const { data, error } = await supabase
          .from('treatment_plans')
          .select('*')
          .eq('patient_id', patientId);
          
        if (error) {
          console.error('Error fetching treatment plans:', error);
          throw error;
        }
        
        console.log("Fetched treatment plans:", data);
        
        // Format the data for the TreatmentHistoryCard
        const formattedTreatments = (data || []).map(treatment => ({
          date: treatment.created_at ? new Date(treatment.created_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }) : 'Unknown date',
          treatment: treatment.title || 'Unnamed Treatment',
          notes: treatment.description || 'No description available',
          amount: treatment.estimated_cost ? `$${treatment.estimated_cost}` : 'N/A'
        }));
        
        setTreatments(formattedTreatments);
      } catch (error) {
        console.error('Error fetching treatment history:', error);
        toast({
          title: "Error loading treatments",
          description: "Failed to load treatment history. Please try again.",
          variant: "destructive"
        });
      } finally {
        setIsLoadingTreatments(false);
      }
    };
    
    fetchTreatmentHistory();
  }, [patientId, toast]);

  const handleAddImages = () => {
    if (!patientId) {
      toast({
        title: "Error",
        description: "Patient ID is missing. Cannot upload images.",
        variant: "destructive",
      });
      return;
    }
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = async (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files) {
        try {
          await uploadFile(files, 'images', { patientId });
          toast({
            title: "Images uploaded successfully",
            description: "The images have been added to the patient's file.",
          });
        } catch (error: any) {
          toast({
            title: "Upload failed",
            description: error?.message || "There was an error uploading the images. Please try again.",
            variant: "destructive",
          });
        }
      }
    };
    input.click();
  };

  const handleUploadFile = () => {
    if (!patientId) {
      toast({
        title: "Error",
        description: "Patient ID is missing. Cannot upload files.",
        variant: "destructive",
      });
      return;
    }
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx';
    input.onchange = async (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files) {
        try {
          await uploadFile(files, 'documents', { patientId });
          toast({
            title: "Document uploaded successfully",
            description: "The document has been added to the patient's file.",
          });
        } catch (error: any) {
          toast({
            title: "Upload failed",
            description: error?.message || "There was an error uploading the document. Please try again.",
            variant: "destructive",
          });
        }
      }
    };
    input.click();
  };

  if (!patientId) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <DoctorHeader />
        <div className="container mx-auto p-4 pb-24 pt-20">
          <Card>
            <CardContent className="py-10">
              <div className="flex flex-col items-center justify-center gap-4 text-center">
                <AlertCircle className="h-12 w-12 text-muted-foreground" />
                <h2 className="text-xl font-semibold">No Patient Selected</h2>
                <p className="text-muted-foreground max-w-md">
                  Please select a patient from your patient list to view their case file.
                </p>
                <Button 
                  onClick={() => navigate(ROUTES.DOCTOR.PATIENTS)} 
                  className="mt-2"
                >
                  <Users className="mr-2 h-4 w-4" />
                  Go to Patient List
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <DoctorFooterNav />
      </div>
    );
  }
  
  // Use the useCaseFile hook to get patient data
  const { patient, isLoading, error, updatePatientDetails } = useCaseFile({ 
    patientId: patientId
  });
  
  // Function to refresh data
  const refreshData = () => {
    // Force re-fetch by re-mounting component
    window.location.reload();
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <DoctorHeader />
        <div className="container mx-auto p-4 pb-24 pt-20 flex flex-col items-center justify-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
          <p className="text-center py-4 text-lg">Loading patient data...</p>
        </div>
        <DoctorFooterNav />
      </div>
    );
  }
  
  if (error || !patient || !patient.id) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <DoctorHeader />
        <div className="container mx-auto p-4 pb-24 pt-20">
          <Card>
            <CardContent className="py-10">
              <div className="flex flex-col items-center justify-center gap-4 text-center">
                <AlertCircle className="h-12 w-12 text-red-500" />
                <h2 className="text-xl font-semibold">Error Loading Patient</h2>
                <p className="text-muted-foreground max-w-md">
                  {error || "Could not load patient data. The patient may not exist or there was an error."}
                </p>
                <div className="flex gap-3 mt-4">
                  <Button variant="outline" onClick={refreshData}>
                    <RefreshCcw className="mr-2 h-4 w-4" />
                    Try Again
                  </Button>
                  <Button onClick={() => navigate(ROUTES.DOCTOR.PATIENTS)}>
                    <Users className="mr-2 h-4 w-4" />
                    Back to Patient List
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        <DoctorFooterNav />
      </div>
    );
  }

  // Patient data is loaded and valid at this point
  const formattedCreatedDate = patient?.created_at || patient?.createdAt || new Date().toISOString();

  const mapPatientStatus = (status: string): 'pending' | 'in progress' | 'completed' | 'archived' => {
    switch (status) {
      case 'in-treatment':
      case 'in-progress':
        return 'in progress';
      case 'completed':
        return 'completed';
      case 'archived':
        return 'archived';
      case 'new':
      case 'pending':
      default:
        return 'pending';
    }
  };

  const patientInfoData = {
    name: patient.name || 'Unknown Patient',
    email: patient.profile?.email || '',
    phone: patient.profile?.phone_number || '',
    condition: patient.condition || 'Not specified',
    notes: Array.isArray(patient.profile?.medicalConditions) 
      ? patient.profile?.medicalConditions.join(', ') 
      : (typeof patient.profile?.medicalConditions === 'string' 
        ? patient.profile.medicalConditions 
        : ''),
    status: mapPatientStatus(patient.status || 'pending')
  };

  const handleSavePatientData = async (updatedData: any) => {
    if (!patientId) {
      toast({
        title: "Missing patient ID",
        description: "Could not update patient data",
        variant: "destructive",
      });
      return;
    }

    try {
      // Use the updatePatientDetails function from the hook
      const result = await updatePatientDetails(updatedData);
      return result;
    } catch (error: any) {
      toast({
        title: "Error updating patient",
        description: error.message || "Failed to update patient information",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <DoctorHeader />

      <div className="container mx-auto p-4 pt-20 pb-24">
        <PatientHeader 
          patientId={patientId} 
          patientName={patient.name || 'Unknown Patient'} 
          createdDate={formattedCreatedDate}
          status={patient.status || 'new'}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex flex-wrap gap-3">
                  <Button onClick={handleAddImages}>
                    <ImageIcon className="h-4 w-4 mr-2" />
                    Add Images
                  </Button>
                  <Button variant="outline" onClick={handleUploadFile}>
                    <FileUp className="h-4 w-4 mr-2" />
                    Upload File
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate(`/doctor-area/follow-up-reports/${patientId}/create`)}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    New Report
                  </Button>
                  <Button
                    onClick={() => navigate(`/doctor-area/appointments?patientId=${patientId}`)}
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    Schedule New Appointment
                  </Button>
                </div>
              </CardContent>
            </Card>

            <PatientInfoCard 
              patientId={patientId} 
              initialData={patientInfoData}
              onSave={handleSavePatientData}
              refetchData={refreshData}
            />

            <TreatmentHistoryCard 
              treatments={treatments} 
              isLoading={isLoadingTreatments}
            />
          </div>
          
          <div className="space-y-6">
            {/* Additional Patient Details or Widgets can be placed here */}
          </div>
        </div>

        <PatientTabView patient={patient} />
      </div>

      <DoctorFooterNav />
    </div>
  );
};

export default CaseFilePage;
